import { IModalConfigInput, Instrument, Position } from '../../../types/shared'
import BaseChart from '../../charts/base_chart'
import { ChartElementType, IndicatorType, OverlayType, SpecificChartFunctionality } from '../../constants/common'
import Indicator from '../../indicators/indicator'
import ChartLayout from '../../models/chart_layout'
import ChartLayoutModel from '../../models/chart_layout'
import Element from '../../models/element'
import Overlay from '../../overlays/overlay'
import { isRedesignEnabled } from '../../utils'
import { getRGBAValueFromValidColorString } from '../../utils/colors'
import { decodeQueryString, encodeQueryString } from '../../utils/query_string'
import { getResponseChartType, removeEmptyIndicatorsAndOverlays } from './chart/model-manipulation'

export interface IIndicatorsValue {
  elementInstance: Indicator
  position: Position
  paneIndex: number
}

export const getOverlayAttributesConfig = (overlay: Overlay) => {
  const parameters: string[] = []
  const color: string[] = []
  overlay.getModalConfig().inputs.forEach((input: IModalConfigInput) => {
    if (input.type === 'color') {
      color.push(getRGBAValueFromValidColorString(input.value.toString()))
    } else {
      parameters.push(input.value.toString())
    }
  })
  return {
    type: overlay.type,
    period: parameters.join(','),
    color: color.join('|'),
  }
}

export const getObjForSubmit = (chartLayoutModel: ChartLayout) => {
  const chartModel = chartLayoutModel.charts().first()
  const quote = chartModel.quote()
  const chartObj = chartModel.toObject()
  const chartElement = chartLayoutModel
    .charts()
    .first()
    .getAllElements()
    .find((element: Element) => element.isChart())!.instance as BaseChart
  const overlays = chartElement.overlays
    .filter((overlay) => overlay.type !== OverlayType.None)
    .map((overlay) => {
      const overlayAttrsConfig = getOverlayAttributesConfig(overlay)
      return {
        name: overlayAttrsConfig.type.replace('overlays/', ''),
        parameters: overlayAttrsConfig.period,
        color: overlayAttrsConfig.color,
      }
    })
  const indicators: IIndicatorsValue[] = []

  let chartIndex: number
  chartModel.getAllPanes().forEach((pane, paneIndex) => {
    pane.getAllElements().forEach((element) => {
      if (element.isChart()) {
        chartIndex = paneIndex
      }
      if (element.isIndicator() && element.instance.type !== IndicatorType.None) {
        const isCot = element.instance.type === IndicatorType.Cot
        if (!isCot || !indicators.some(({ elementInstance }) => elementInstance.type === IndicatorType.Cot)) {
          indicators.push({
            // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
            elementInstance: element.instance,
            position: chartIndex === undefined ? Position.Above : Position.Below,
          })
        }
      }
    })
  })

  const response = {
    settings: {
      premarket: quote.premarket,
      aftermarket: quote.aftermarket,
      scale: chartModel.scale,

      style: getResponseChartType(chartElement.type as ChartElementType),
      overlays,
      indicators: indicators.map((indicator) => {
        const parameters: string[] = []
        indicator.elementInstance.getModalConfig().inputs.forEach((input: IModalConfigInput) => {
          parameters.push(input.value.toString())
        })
        return {
          parameters: parameters.join(','),
          position: indicator.position,
          name: indicator.elementInstance.type.replace('indicators/', ''),
        }
      }),
    },
    instrument: [SpecificChartFunctionality.quotePage, SpecificChartFunctionality.chartPage].includes(
      chartLayoutModel.specificChartFunctionality
    )
      ? Instrument.Stock
      : (chartObj.instrument as Instrument),
  }
  return response
}

export const isChartModelValid = (chartLayoutModel: ChartLayout) => {
  removeEmptyIndicatorsAndOverlays(chartLayoutModel)
  let isValid = true
  const chartModel = chartLayoutModel.charts().first()
  const chartElement = chartModel.getAllElements().find((element: Element) => element.isChart())!.instance as BaseChart
  chartElement.overlays.forEach((overlay) => {
    overlay.getModalConfig().inputs.forEach((input: IModalConfigInput) => {
      if (!overlay.getIsValid(input.name)) isValid = false
    })
  })

  chartModel.getAllElements().forEach((element) => {
    if (element.isIndicator()) {
      // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
      const elementInstance = element.instance as Indicator
      elementInstance.getModalConfig().inputs.forEach((input: IModalConfigInput) => {
        if (!elementInstance.getIsValid(input.name)) isValid = false
      })
    }
  })
  return isValid
}

const getQuoteUrlChartType = (chartElementType: ChartElementType) => {
  switch (chartElementType) {
    case ChartElementType.LineChart:
      return 'l'
    case ChartElementType.OhlcChart:
      return 'o'
    case ChartElementType.HeikinAshi:
      return 'ha'
    case ChartElementType.HollowCandleStick:
      return 'hc'
    case ChartElementType.CandleStick:
    default:
      return 'c'
  }
}

export const handleUrlUpdateOnSettingsSave = (chartLayoutModel: ChartLayoutModel) => {
  if (chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage) return

  const chartType =
    chartLayoutModel &&
    (chartLayoutModel
      ?.charts()
      .first()
      ?.getAllElements()
      .find((element: Element) => element.isChart())?.instance.type as ChartElementType)
  const decodedQuery = decodeQueryString()
  const isDeprecatedInteractiveParam = !isRedesignEnabled() && decodedQuery.ty === '1'
  const encodeSearchString = encodeQueryString({
    ...decodeQueryString(),
    ty: isDeprecatedInteractiveParam ? 1 : getQuoteUrlChartType(chartType),
  })
  document.location = `${window.location.pathname}?${encodeSearchString}`
}
